import React, { useEffect, useState } from 'react';
import { Plus, Save, X } from 'lucide-react';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { addDays, errorCode, formatDay, todayKey } from './appointmentUtils';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Une journée vue par le garage : matin et (éventuellement) après-midi, séparés par la pause déjeuner
interface DayUi { open: boolean; m1: string; m2: string; afternoon: boolean; a1: string; a2: string; slotDuration: number }
interface Closure { from: string; to: string; label?: string }

const fromServer = (d: any): DayUi => {
    const lunch = d?.lunchStart && d?.lunchEnd;
    return {
        open: !d?.closed,
        m1: d?.open || '08:30', m2: lunch ? d.lunchStart : (d?.close || '12:00'),
        afternoon: !!lunch, a1: lunch ? d.lunchEnd : '14:00', a2: d?.close || '18:00',
        slotDuration: d?.slotDuration || 30
    };
};
const toServer = (u: DayUi, bays: number) => ({
    closed: !u.open, open: u.m1, close: u.afternoon ? u.a2 : u.m2,
    lunchStart: u.afternoon ? u.m2 : null, lunchEnd: u.afternoon ? u.a1 : null,
    slotDuration: u.slotDuration, maxAppointmentsPerSlot: bays
});

interface Props { onSaved?: (cfg: any) => void }

// Disponibilités du garage : horaires (matin / après-midi), capacité et règles, validation, fermetures, aperçu client
export const AvailabilitySettings: React.FC<Props> = ({ onSaved }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [cfg, setCfg] = useState<any>(null);
    const [days, setDays] = useState<Record<string, DayUi>>({});
    const [rules, setRules] = useState<any>({});
    const [closures, setClosures] = useState<Closure[]>([]);
    const [newClosure, setNewClosure] = useState<Closure | null>(null);
    const [preview, setPreview] = useState<{ date: string; slots: string[]; duration: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

    const hydrate = (c: any) => {
        setCfg(c);
        setDays(Object.fromEntries(DAYS.map(d => [d, fromServer(c.schedule?.[d])])));
        setRules({
            bays: Math.max(1, ...DAYS.map(d => c.schedule?.[d]?.maxAppointmentsPerSlot || 1)),
            minNoticeHours: c.minNoticeHours ?? 2, weeks: Math.max(1, Math.round((c.maxAdvanceBookingDays || 30) / 7)),
            bufferBetweenSlots: c.bufferBetweenSlots ?? 0, defaultDuration: c.defaultDuration || 60, autoConfirm: !!c.autoConfirm,
            holdHours: c.holdHours ?? 2, requestExpiryHours: c.requestExpiryHours ?? 48, freeCancellationHours: c.freeCancellationHours ?? 24
        });
        setClosures((c.closures || []).map((x: any) => ({ from: String(x.from).slice(0, 10), to: String(x.to).slice(0, 10), label: x.label })));
    };

    useEffect(() => {
        appointmentService.getConfig().then(r => hydrate(r.data)).catch(() => setMessage({ type: 'error', text: t('appointments.errors.generic') }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aperçu côté client : créneaux d'un prochain jour ouvert pour la durée par défaut (configuration enregistrée)
    useEffect(() => {
        if (!cfg) return;
        let cancelled = false;
        (async () => {
            for (let i = 1; i <= 14; i++) {
                const key = addDays(todayKey(), i);
                try {
                    const r = await appointmentService.getAvailableSlots(key, cfg.defaultDuration || 60);
                    if (cancelled) return;
                    if (!r.data?.closed && (r.data?.availableSlots || []).length) {
                        setPreview({ date: key, slots: r.data.availableSlots.slice(0, 8), duration: r.data.duration });
                        return;
                    }
                } catch { return; }
            }
            if (!cancelled) setPreview(null);
        })();
        return () => { cancelled = true; };
    }, [cfg]);

    if (!cfg) return <p className="text-sm text-gray-500">{message?.text || t('appointments.form.loadingSlots')}</p>;

    const setDay = (d: string, patch: Partial<DayUi>) => setDays(s => ({ ...s, [d]: { ...s[d], ...patch } }));
    const setRule = (k: string, v: any) => setRules((r: any) => ({ ...r, [k]: v }));

    const save = async () => {
        setSaving(true); setMessage(null);
        try {
            const res = await appointmentService.updateConfig({
                schedule: Object.fromEntries(DAYS.map(d => [d, toServer(days[d], rules.bays)])),
                bays: rules.bays, minNoticeHours: Number(rules.minNoticeHours), maxAdvanceBookingDays: rules.weeks * 7,
                bufferBetweenSlots: Number(rules.bufferBetweenSlots), defaultDuration: Number(rules.defaultDuration),
                autoConfirm: !!rules.autoConfirm, holdHours: Number(rules.holdHours),
                requestExpiryHours: Number(rules.requestExpiryHours), freeCancellationHours: Number(rules.freeCancellationHours),
                closures
            });
            hydrate(res.data); onSaved?.(res.data);
            setMessage({ type: 'ok', text: t('appointments.settings.saved') });
        } catch (err) {
            setMessage({ type: 'error', text: t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') }) });
        } finally { setSaving(false); }
    };

    const addClosure = () => {
        if (newClosure?.from && newClosure?.to && newClosure.from <= newClosure.to) { setClosures(c => [...c, newClosure]); setNewClosure(null); }
    };

    const card = 'bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5';
    const input = 'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500';
    const label = 'block text-xs font-semibold text-gray-700 mb-1';
    const range = (a: string, b: string, set: (k: 'a' | 'b', v: string) => void, off: boolean) => (
        <div className={`flex items-center gap-1.5 ${off ? 'opacity-40' : ''}`}>
            <input type="time" value={a} disabled={off} onChange={e => set('a', e.target.value)} className={`${input} w-[6.2rem]`} />
            <span className="text-gray-400">–</span>
            <input type="time" value={b} disabled={off} onChange={e => set('b', e.target.value)} className={`${input} w-[6.2rem]`} />
        </div>
    );

    return (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] items-start" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="space-y-4">
                <section className={card}>
                    <h3 className="font-bold text-gray-900">{t('appointments.settings.hours')}</h3>
                    <p className="text-sm text-gray-500 mb-3">{t('appointments.settings.hoursHint')}</p>
                    <div className="hidden md:grid grid-cols-[7rem_4rem_1fr_1fr] gap-3 text-xs text-gray-500 pb-1">
                        <span>{t('appointments.settings.day')}</span><span>{t('appointments.settings.open')}</span><span>{t('appointments.settings.morning')}</span><span>{t('appointments.settings.afternoon')}</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {DAYS.map(d => {
                            const u = days[d];
                            return (
                                <div key={d} className="py-2.5 grid grid-cols-[1fr_auto] md:grid-cols-[7rem_4rem_1fr_1fr] gap-x-3 gap-y-2 items-center">
                                    <span className="font-bold text-gray-900 text-sm">{t(`appointments.settings.days.${d}`)}</span>
                                    <button type="button" role="switch" aria-checked={u.open} aria-label={t(`appointments.settings.days.${d}`)} onClick={() => setDay(d, { open: !u.open })}
                                            className={`relative w-11 h-6 rounded-full transition-colors justify-self-end md:justify-self-start ${u.open ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${u.open ? (isRTL ? 'right-[1.375rem]' : 'left-[1.375rem]') : (isRTL ? 'right-0.5' : 'left-0.5')}`} />
                                    </button>
                                    <div className="col-span-2 md:col-span-1">{u.open ? range(u.m1, u.m2, (k, v) => setDay(d, k === 'a' ? { m1: v } : { m2: v }), false) : <span className="text-sm text-gray-400">{t('appointments.settings.closed')}</span>}</div>
                                    <div className="col-span-2 md:col-span-1 flex items-center gap-2">
                                        {u.open && u.afternoon ? (<>
                                            {range(u.a1, u.a2, (k, v) => setDay(d, k === 'a' ? { a1: v } : { a2: v }), false)}
                                            <button type="button" onClick={() => setDay(d, { afternoon: false })} aria-label={t('appointments.settings.closeAfternoon')} className="p-1 rounded text-gray-400 hover:text-red-600"><X size={14} /></button>
                                        </>) : u.open ? (
                                            <button type="button" onClick={() => setDay(d, { afternoon: true })} className="text-sm text-gray-400 hover:text-indigo-600 inline-flex items-center gap-1">{t('appointments.settings.closed')} <Plus size={12} /></button>
                                        ) : <span className="text-sm text-gray-400">{t('appointments.settings.closed')}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className={card}>
                    <h3 className="font-bold text-gray-900 mb-3">{t('appointments.settings.capacity')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div><label className={label}>{t('appointments.settings.bays')}</label><input type="number" min={1} max={20} value={rules.bays} onChange={e => setRule('bays', Math.max(1, Number(e.target.value) || 1))} className={`${input} w-full`} /></div>
                        <div><label className={label}>{t('appointments.settings.minNotice')}</label>
                            <select value={rules.minNoticeHours} onChange={e => setRule('minNoticeHours', Number(e.target.value))} className={`${input} w-full`}>
                                {[0, 1, 2, 4, 12, 24, 48].map(h => <option key={h} value={h}>{t('appointments.settings.hoursOption', { count: h })}</option>)}
                            </select></div>
                        <div><label className={label}>{t('appointments.settings.bookableUntil')}</label>
                            <select value={rules.weeks} onChange={e => setRule('weeks', Number(e.target.value))} className={`${input} w-full`}>
                                {[1, 2, 3, 4, 6, 8, 12].map(w => <option key={w} value={w}>{t('appointments.settings.weeksOption', { count: w })}</option>)}
                            </select></div>
                        <div><label className={label}>{t('appointments.settings.buffer')}</label>
                            <select value={rules.bufferBetweenSlots} onChange={e => setRule('bufferBetweenSlots', Number(e.target.value))} className={`${input} w-full`}>
                                {[0, 10, 15, 30, 45].map(m => <option key={m} value={m}>{m} min</option>)}
                            </select></div>
                        <div><label className={label}>{t('appointments.settings.defaultDuration')}</label>
                            <select value={rules.defaultDuration} onChange={e => setRule('defaultDuration', Number(e.target.value))} className={`${input} w-full`}>
                                {[30, 45, 60, 90, 120, 180, 240].map(m => <option key={m} value={m}>{m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}` : `${m} min`}</option>)}
                            </select></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">{t('appointments.settings.defaultDurationHint')}</p>
                </section>

                <section className={card}>
                    <h3 className="font-bold text-gray-900 mb-3">{t('appointments.settings.validation')}</h3>
                    <div className="space-y-2">
                        {[true, false].map(auto => (
                            <label key={String(auto)} className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${rules.autoConfirm === auto ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200'}`}>
                                <input type="radio" name="validation" checked={rules.autoConfirm === auto} onChange={() => setRule('autoConfirm', auto)} className="mt-1 text-indigo-600 focus:ring-indigo-500" />
                                <span className="text-sm">
                                    <span className="block font-bold text-gray-900">{auto ? t('appointments.settings.autoConfirm') : t('appointments.settings.manualConfirm')}</span>
                                    <span className="block text-gray-600">{auto ? t('appointments.settings.autoConfirmHint') : t('appointments.settings.manualConfirmHint', { count: rules.holdHours })}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                        <div><label className={label}>{t('appointments.settings.holdHours')}</label><input type="number" min={1} value={rules.holdHours} onChange={e => setRule('holdHours', Number(e.target.value))} className={`${input} w-full`} /></div>
                        <div><label className={label}>{t('appointments.settings.requestExpiryHours')}</label><input type="number" min={1} value={rules.requestExpiryHours} onChange={e => setRule('requestExpiryHours', Number(e.target.value))} className={`${input} w-full`} /></div>
                        <div><label className={label}>{t('appointments.settings.freeCancellationHours')}</label><input type="number" min={0} value={rules.freeCancellationHours} onChange={e => setRule('freeCancellationHours', Number(e.target.value))} className={`${input} w-full`} /></div>
                    </div>
                </section>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-4">
                <section className={card}>
                    <h3 className="font-bold text-gray-900 mb-2">{t('appointments.settings.closures')}</h3>
                    <ul className="space-y-1.5">
                        {closures.map((c, i) => (
                            <li key={i} className="flex items-center justify-between gap-2 text-sm">
                                <span className="text-gray-800">{c.from === c.to ? formatDay(c.from, currentLanguage, { day: 'numeric', month: 'short' }) : `${formatDay(c.from, currentLanguage, { day: 'numeric', month: 'short' })} – ${formatDay(c.to, currentLanguage, { day: 'numeric', month: 'short' })}`}</span>
                                <span className="flex items-center gap-2 text-gray-500">{c.label}<button type="button" onClick={() => setClosures(list => list.filter((_, idx) => idx !== i))} aria-label={t('appointments.actions.close')} className="text-gray-400 hover:text-red-600"><X size={14} /></button></span>
                            </li>
                        ))}
                        {closures.length === 0 && <li className="text-sm text-gray-400">{t('appointments.settings.noClosure')}</li>}
                    </ul>
                    {newClosure ? (
                        <div className="mt-3 space-y-2">
                            <div className="flex gap-2"><input type="date" value={newClosure.from} onChange={e => setNewClosure({ ...newClosure, from: e.target.value, to: newClosure.to < e.target.value ? e.target.value : newClosure.to })} className={`${input} flex-1 min-w-0`} /><input type="date" value={newClosure.to} min={newClosure.from} onChange={e => setNewClosure({ ...newClosure, to: e.target.value })} className={`${input} flex-1 min-w-0`} /></div>
                            <input placeholder={t('appointments.settings.closureLabel')} value={newClosure.label || ''} onChange={e => setNewClosure({ ...newClosure, label: e.target.value })} className={`${input} w-full`} />
                            <div className="flex gap-2"><button type="button" onClick={addClosure} className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold">{t('appointments.settings.add')}</button><button type="button" onClick={() => setNewClosure(null)} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100">{t('appointments.actions.back')}</button></div>
                        </div>
                    ) : (
                        <button type="button" onClick={() => { const k = todayKey(); setNewClosure({ from: k, to: k, label: '' }); }} className="mt-3 text-sm font-semibold text-indigo-600 hover:underline">+ {t('appointments.settings.addClosure')}</button>
                    )}
                </section>

                <section className={card}>
                    <h3 className="font-bold text-gray-900">{t('appointments.settings.preview')}</h3>
                    {preview ? (<>
                        <p className="text-sm text-gray-500 mt-1">{t('appointments.settings.previewFor', { duration: preview.duration >= 60 ? `${Math.floor(preview.duration / 60)} h${preview.duration % 60 ? ` ${preview.duration % 60}` : ''}` : `${preview.duration} min`, date: formatDay(preview.date, currentLanguage, { weekday: 'long', day: 'numeric', month: 'short' }) })}</p>
                        <div className="flex flex-wrap gap-2 mt-2">{preview.slots.map(s => <span key={s} className="px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 text-sm font-semibold">{s}</span>)}</div>
                    </>) : <p className="text-sm text-gray-400 mt-1">{t('appointments.form.noSlots')}</p>}
                </section>

                <div className="flex items-center gap-3">
                    <button disabled={saving} onClick={save} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"><Save size={16} />{t('appointments.settings.save')}</button>
                </div>
                {message && <p role="status" className={`text-sm ${message.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}
            </aside>
        </div>
    );
};
